if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        // WS5 (PRD #116): guard the querySelector chains. Edge's page-translate
        // feature (cloneNodeWithPlaceholders) can mutate/replace the DOM so the
        // expected <form>, [name=id] input, or submit button are absent; without
        // these guards the constructor throws and aborts the element.
        this.form = this.querySelector("form");
        if (!this.form) return;

        var idInput = this.form.querySelector("[name=id]");
        if (idInput) idInput.disabled = false;

        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');

        if (this.submitButton && document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // /cart/add.js increments, so it is never blind-retried — an in-app-webview
        // abort can fire AFTER the server already took the add, and replaying it
        // would add the shopper's item twice. CartAddGuard reconciles against the
        // server's cart and only replays an add it can prove never landed.
        const guardedAdd =
          window.CartAddGuard && window.CartAddGuard.cartAdd;

        // Tracking ids onto the cart first (n2t-ad-intel-desk#5) so they ride
        // checkout into note_attributes; with no cart yet this sends nothing and
        // afterAddToCart() below writes them. Best-effort: resolves within ~800ms
        // and never rejects, so a slow or failed write can't hold or break the add.
        const cartAttributes = window.N2TCartAttributes;
        const attributesReady =
          cartAttributes && typeof cartAttributes.beforeAddToCart === "function"
            ? Promise.resolve(cartAttributes.beforeAddToCart()).catch(() => {})
            : Promise.resolve();

        const addRequest = attributesReady.then(() =>
          guardedAdd
            ? guardedAdd(`${routes.cart_add_url}`, config, { step: "pdp-add-to-cart" }).then(
                ({ response, alreadyApplied }) => {
                  if (!alreadyApplied) return response.json();
                  // The add landed but its response died with the page. There is no
                  // add response to render from, so rebuild the same shape from the
                  // server's actual cart plus freshly rendered sections.
                  return window.CartAddGuard.cartStateWithSections(
                    this.cart
                      ? this.cart.getSectionsToRender().map((section) => section.id)
                      : []
                  );
                }
              )
            : fetch(`${routes.cart_add_url}`, config).then((response) => response.json())
        );

        addRequest
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            }

            // The add made sure a cart exists, so the tracking ids go out now.
            // beforeAddToCart() sends nothing while there is no cart yet: an
            // attribute write must never create one (it would race the add's
            // own cart creation). Capped at ~800ms and never rejects.
            const attributesWritten =
              cartAttributes && typeof cartAttributes.afterAddToCart === "function"
                ? Promise.resolve(cartAttributes.afterAddToCart()).catch(() => {})
                : Promise.resolve();

            if (!this.cart) {
              //window.location = window.routes.cart_url;
              return attributesWritten.then(() => {
                window.location = "/checkout/";
              });
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            // The add could not be completed or safely replayed. Say so, and offer a
            // retry — instead of the silent dead-end (button re-enables, cart never
            // changes, shopper has no idea why) that this path used to leave behind.
            // The add landed and only the cart refresh failed: a retry would add
            // the item twice.
            if (e && e.addApplied) {
              // The drawer is stale, so a second click would add the item again
              // unseen. Keep the button held until the page is refreshed.
              this.holdUntilReload = true;
              // The add created or changed the cart, so stamp the tracking ids as
              // the success path would. Best-effort, never rejects.
              if (cartAttributes && typeof cartAttributes.afterAddToCart === "function") {
                Promise.resolve(cartAttributes.afterAddToCart()).catch(() => {});
              }
              if (window.NttUtils && window.NttUtils.showCartError) {
                window.NttUtils.showCartError("Added to your cart. Refresh the page to see it.");
              }
              return;
            }
            // A timed-out add can still land at Shopify after the abort. Hold the
            // button and skip the retry action until it has had time to settle.
            if (e && e.mayHaveApplied) {
              this.addMaySettle = true;
              if (window.NttUtils && window.NttUtils.showCartError) {
                window.NttUtils.showCartError(
                  "We couldn't confirm that was added. Please check your cart in a moment before trying again."
                );
              }
              return;
            }
            if (window.NttUtils && window.NttUtils.showCartError) {
              window.NttUtils.showCartError(
                "We couldn't add that to your cart. Please check your connection and try again.",
                { onRetry: () => this.onSubmitHandler(evt) }
              );
            }
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (this.holdUntilReload) {
              // Left disabled on purpose; the toast asks for a refresh.
            } else if (this.addMaySettle) {
              this.addMaySettle = false;
              setTimeout(() => this.refreshAfterUncertainAdd(), 15000);
            } else if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");
          });
      }

      // A timed-out add can land after the guard's cart read, so once it has had
      // time to settle, re-read the server cart and redraw the drawer before
      // offering the button again: a stale drawer would hide a landed add and
      // invite a second one. If the cart can't be read, keep the button held.
      // The drawer is redrawn in place, not opened: the shopper didn't ask for it
      // 15 seconds on, and the add may not have landed, so its empty state comes
      // from the re-read. The cart notification renders one added line by its
      // key, which a cart read doesn't carry, so with no drawer only the cart
      // bubble is redrawn.
      refreshAfterUncertainAdd() {
        const guard = window.CartAddGuard;
        const drawer = this.cart && this.cart.localName === "cart-drawer" ? this.cart : null;
        const sectionIds = drawer
          ? drawer.getSectionsToRender().map((section) => section.id)
          : ["cart-icon-bubble"];
        const refresh =
          guard && typeof guard.cartStateWithSections === "function"
            ? Promise.resolve().then(() => guard.cartStateWithSections(sectionIds))
            : Promise.reject(new Error("CartAddGuard unavailable"));
        return refresh
          .then((state) => {
            // The add landed late, past the success path that stamps the
            // tracking ids, so stamp them now, before a redraw that can throw:
            // the item is in the cart whether or not the drawer redraws.
            // Best-effort, never rejects.
            const cartAttributes = window.N2TCartAttributes;
            if (state && state.item_count > 0 && cartAttributes && typeof cartAttributes.afterAddToCart === "function") {
              Promise.resolve()
                .then(() => cartAttributes.afterAddToCart())
                .catch(() => {});
            }
            if (drawer && state) this.redrawDrawer(drawer, state);
            else if (state) this.renderCartBubble(state);
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
          })
          .catch(() => {
            this.holdUntilReload = true;
            if (window.NttUtils && window.NttUtils.showCartError) {
              window.NttUtils.showCartError(
                "We couldn't confirm your cart. Refresh the page to check it before adding again."
              );
            }
          });
      }

      // Every drawer surface must redraw, or the refresh counts as failed and Add
      // stays held: a half-stale drawer is the double-add risk this guards. The
      // overlay is replaced with #CartDrawer, so its close handler is re-bound,
      // as CartDrawer.renderContents does.
      redrawDrawer(drawer, state) {
        const updates = drawer.getSectionsToRender().map((section) => {
          const html = state.sections && state.sections[section.id];
          const target = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          const source = html
            ? new DOMParser()
                .parseFromString(html, "text/html")
                .querySelector(section.selector || ".shopify-section")
            : null;
          if (!target || !source) throw new Error(`cart section ${section.id} missing`);
          return [target, source.innerHTML];
        });
        updates.forEach(([target, html]) => {
          target.innerHTML = html;
        });
        drawer.classList.toggle("is-empty", state.item_count === 0);
        const overlay = drawer.querySelector("#CartDrawer-Overlay");
        if (overlay && typeof drawer.close === "function") {
          overlay.addEventListener("click", drawer.close.bind(drawer));
        }
      }

      // Missing or malformed bubble markup fails the refresh, like the drawer. A
      // page with no bubble has no stale count to show, so that alone is fine.
      renderCartBubble(state) {
        const html = state.sections && state.sections["cart-icon-bubble"];
        const section = html
          ? new DOMParser().parseFromString(html, "text/html").querySelector(".shopify-section")
          : null;
        if (!section) throw new Error("cart section cart-icon-bubble missing");
        const bubble = document.getElementById("cart-icon-bubble");
        if (bubble) bubble.innerHTML = section.innerHTML;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
